// Instrument catalog for the configurable map-bottom data strip.
//
// Single source of truth: each entry below is one optional readout.
// Adding a new instrument = one entry here, no other code changes
// required (the DataStrip and the InstrumentSelector menu render
// straight from INSTRUMENT_CATALOG).
//
// Wave 1 (live): all the instruments derivable from current GPS +
// active flight plan + active aircraft profile + a handful of small
// async data fetches (terrain elevation, nearest METAR, nearest
// airport/navaid). About 22 of the 30+ items the boss listed.
//
// Wave 2 (placeholders, marked `available: false`): Grid MORA, Height
// MEF, Cabin Pressure, G-Meter. These need additional ingestion or
// hardware integration; they appear in the menu so users know they're
// coming, with a tooltip explaining what's needed. See
// docs/avionics-integration-research.md for the GDL90 path that
// unlocks G-Meter, Pressure Altitude, and Cabin Pressure (some).
//
// Each compute() function takes an InstrumentContext and returns a
// short string for display in the strip. Use '\u2014' (em-dash) to
// indicate "not available right now" so the visual rhythm of the
// strip stays consistent.

import type { GpsFix } from '../stores/gpsStore';
import type { PlanPoint } from '../stores/planStore';
import { greatCircleNm, bearingDeg } from './geo';
import { trueToMagnetic } from './flightCalc';
import { formatLocalTime } from './timeFormat';

// --- Compute context -----------------------------------------------------

export interface InstrumentContext {
  fix: GpsFix | null;
  /** Most-recent previous fix, used for derivatives like vertical speed
   *  and rate-of-turn. Null on first GPS sample. */
  prevFix: GpsFix | null;
  /** Track-recording start timestamp (ms). null if not recording \u2014
   *  in which case 'flight time' falls back to '\u2014'. */
  flightStartedAtMs: number | null;
  /** Active flight plan route (>= 0 points). */
  route: PlanPoint[];
  /** Active flight-plan cruise altitude (ft MSL). */
  cruiseAlt: number | null;
  /** Active aircraft profile cruise TAS (kt) for ETE math. */
  cruiseKt: number | null;

  /** Index of the current 'next' waypoint (route[idx]) the aircraft
   *  is heading toward. -1 when no plan or past the destination. */
  nextWpIdx: number;

  /** Async-resolved terrain elevation (ft MSL) at fix.lat/lon \u2014
   *  populated by the AGL fetcher. null while pending or unavailable. */
  terrainElevFt: number | null;

  /** Async-resolved nearest METAR's altimeter setting (inHg). null
   *  while pending or unavailable. */
  baroInHg: number | null;
  /** Source station for the baro reading (e.g. 'KSIY'). */
  baroStation: string | null;

  /** Async-resolved nearest airport. */
  nearestAirport: { ident: string; distNm: number } | null;
  /** Async-resolved nearest navaid. */
  nearestNavaid: { ident: string; distNm: number; type: string } | null;

  /** Local magnetic variation in degrees east-positive. Defaults to 15
   *  for west-coast US until we wire NASR magvar lookup at the fix
   *  point. */
  magVarDeg: number;
}

// --- Catalog entry shape -------------------------------------------------

export interface InstrumentDef {
  /** Stable id used as React key, persistence key, and the
   *  discriminator the menu shows. NEVER renumber or rename \u2014 clients
   *  may have these in localStorage. */
  id: InstrumentId;
  /** Long, human-friendly name for the InstrumentSelector menu. */
  label: string;
  /** Compact label rendered above the value on the strip (tracking-widest). */
  shortLabel: string;
  /** Optional explanatory tooltip / sublabel for the picker menu. */
  description?: string;
  /** True when the instrument is actually computable today.
   *  Wave 2 placeholders set this to false; the menu still shows them
   *  but renders them as locked. */
  available: boolean;
  /** Group used to sort the picker into sections. */
  group: InstrumentGroup;
  /** Pure function: takes the live context, returns the displayed value. */
  compute: (ctx: InstrumentContext) => string;
}

export type InstrumentGroup =
  | 'position'    // lat/lon/alt/track/zulu time
  | 'aircraft'   // GS/VS/turn rate/G/headings
  | 'navigation'  // next/dest/CTE/bearings/courses
  | 'altitude'   // AGL/MEF/MORA/baro/pressure
  | 'time'       // flight time/ETE/ETA
  | 'environment'; // horizon/coords/nearest

export type InstrumentId =
  // Position group
  | 'lat' | 'lon' | 'currCoords' | 'gpsAlt' | 'track' | 'trackTrue' | 'gpsAcc' | 'zuluTime' | 'localTime'
  // Aircraft group
  | 'gs' | 'vs' | 'turnRate' | 'gMeter'
  // Altitude group
  | 'aglFt' | 'mefFt' | 'moraFt' | 'baroAlt' | 'pressureAlt' | 'cabinPress' | 'nearestBaro'
  // Navigation group
  | 'eteNext' | 'etaNext' | 'distNext' | 'brgNext' | 'crsNext' | 'cte'
  | 'eteDest' | 'etaDest' | 'distDest' | 'brgDest' | 'descentDest'
  | 'nearestApt' | 'nearestNav'
  // Time group
  | 'flightTime'
  // Environment group
  | 'horizonDist'
  ;

// --- Helpers (private) ---------------------------------------------------

const M_TO_FT = 3.28084;
const MPS_TO_KT = 1.94384;
const EM = '\u2014';

function pad3(n: number): string {
  return Math.round(n).toString().padStart(3, '0');
}

function formatHm(min: number): string {
  if (!Number.isFinite(min)) return EM;
  if (min < 0) return EM;
  const h = Math.floor(min / 60);
  const m = Math.round(min - h * 60);
  if (h === 0) return `${m}m`;
  return `${h}h${m.toString().padStart(2, '0')}m`;
}

function formatHms(secTotal: number): string {
  if (!Number.isFinite(secTotal) || secTotal < 0) return EM;
  const h = Math.floor(secTotal / 3600);
  const m = Math.floor((secTotal % 3600) / 60);
  const s = Math.floor(secTotal % 60);
  if (h === 0) return `${m}:${s.toString().padStart(2, '0')}`;
  return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

/** Cross-track error in nautical miles. Positive = right of course. */
function crossTrackErrorNm(
  fix: { lat: number; lon: number },
  legFrom: { lat: number; lon: number },
  legTo: { lat: number; lon: number },
): number {
  const R_NM = 3440.065;
  // Bearing from leg start to current pos vs leg start to leg end.
  const brgAToPos = bearingDeg(legFrom.lat, legFrom.lon, fix.lat, fix.lon);
  const brgAToB   = bearingDeg(legFrom.lat, legFrom.lon, legTo.lat, legTo.lon);
  const distAToPos = greatCircleNm(legFrom.lat, legFrom.lon, fix.lat, fix.lon);
  const dXt = Math.asin(
    Math.sin(distAToPos / R_NM) * Math.sin(((brgAToPos - brgAToB) * Math.PI) / 180)
  ) * R_NM;
  return dXt;
}

/** Required descent rate (fpm) to reach destElevFt at distNm @ groundspeed. */
function descentFpm(currentAltFt: number, destElevFt: number, distNm: number, gsKt: number): number | null {
  if (!Number.isFinite(currentAltFt) || !Number.isFinite(destElevFt)) return null;
  if (gsKt <= 0 || distNm <= 0) return null;
  const eteHr = distNm / gsKt;
  const eteMin = eteHr * 60;
  if (eteMin <= 0) return null;
  const altToLose = currentAltFt - destElevFt;
  return altToLose / eteMin;
}

// --- Catalog -------------------------------------------------------------

export const INSTRUMENT_CATALOG: InstrumentDef[] = [
  // ---- Position ----
  {
    id: 'gpsAlt', label: 'GPS Altitude', shortLabel: 'ALT',
    description: 'Geometric altitude from GPS, ft MSL.',
    group: 'position', available: true,
    compute: (c) => c.fix?.altMeters != null
      ? `${(c.fix.altMeters * M_TO_FT).toFixed(0)} ft` : EM,
  },
  {
    id: 'track', label: 'Track', shortLabel: 'TRK',
    // FAA / ATC / sectional charts all use magnetic. Every certified
    // GA avionics box (Garmin G500/G1000, Avidyne, etc.) renders the
    // ground track in magnetic by default. Fyrefly does the same so a
    // pilot's instrument scan stays consistent across the panel and
    // the iPad. Use the dedicated 'trackTrue' instrument for the true
    // value (handy for IFR enroute when the cross-check is great-
    // circle math).
    description: 'GPS-derived ground track (magnetic degrees).',
    group: 'position', available: true,
    compute: (c) => {
      if (c.fix?.headingDeg == null) return EM;
      const mag = trueToMagnetic(c.fix.headingDeg, c.magVarDeg);
      return `${pad3(mag)}\u00b0`;
    },
  },
  {
    id: 'trackTrue', label: 'Track (True)', shortLabel: 'TRKT',
    description: 'GPS-derived ground track in true degrees. Useful for IFR enroute cross-checks; most pilots want the regular magnetic TRK instead.',
    group: 'position', available: true,
    compute: (c) => c.fix?.headingDeg != null ? `${pad3(c.fix.headingDeg)}\u00b0` : EM,
  },
  {
    id: 'lat', label: 'Latitude', shortLabel: 'LAT',
    description: 'Current GPS latitude.',
    group: 'position', available: true,
    compute: (c) => c.fix ? c.fix.lat.toFixed(4) : EM,
  },
  {
    id: 'lon', label: 'Longitude', shortLabel: 'LON',
    description: 'Current GPS longitude.',
    group: 'position', available: true,
    compute: (c) => c.fix ? c.fix.lon.toFixed(4) : EM,
  },
  {
    id: 'currCoords', label: 'Current Coords (DMM)', shortLabel: 'COORDS',
    description: 'Lat/Lon in degrees-decimal-minutes (Forest Service standard).',
    group: 'position', available: true,
    compute: (c) => {
      if (!c.fix) return EM;
      const fmt = (v: number, isLat: boolean) => {
        const hemi = v < 0 ? (isLat ? 'S' : 'W') : (isLat ? 'N' : 'E');
        const abs = Math.abs(v);
        const deg = Math.floor(abs);
        const min = (abs - deg) * 60;
        return `${hemi}${deg}\u00b0${min.toFixed(1)}'`;
      };
      return `${fmt(c.fix.lat, true)} ${fmt(c.fix.lon, false)}`;
    },
  },
  {
    id: 'gpsAcc', label: 'GPS Accuracy', shortLabel: 'GPS\u00b1',
    description: 'GPS fix accuracy (1-sigma), meters.',
    group: 'position', available: true,
    compute: (c) => c.fix?.accuracyM != null ? `\u00b1${Math.round(c.fix.accuracyM)} m` : EM,
  },
  {
    id: 'zuluTime', label: 'Zulu Time', shortLabel: 'ZULU',
    description: 'Current UTC time, HH:MM:SS.',
    group: 'position', available: true,
    compute: () => {
      const d = new Date();
      const h = d.getUTCHours().toString().padStart(2, '0');
      const m = d.getUTCMinutes().toString().padStart(2, '0');
      const s = d.getUTCSeconds().toString().padStart(2, '0');
      return `${h}:${m}:${s}Z`;
    },
  },
  {
    id: 'localTime', label: 'Local Time', shortLabel: 'LCL',
    description: 'Current local time (device timezone), HH:MM:SS.',
    group: 'position', available: true,
    compute: () => {
      const d = new Date();
      const h = d.getHours().toString().padStart(2, '0');
      const m = d.getMinutes().toString().padStart(2, '0');
      const s = d.getSeconds().toString().padStart(2, '0');
      // Wiley feedback 2026-05-12: pilots often want local time
      // alongside Zulu; let them pick either or both for the strip.
      return `${h}:${m}:${s}`;
    },
  },

  // ---- Aircraft ----
  {
    id: 'gs', label: 'Groundspeed', shortLabel: 'GS',
    description: 'GPS-derived groundspeed (knots).',
    group: 'aircraft', available: true,
    compute: (c) => c.fix?.speedMps != null ? `${(c.fix.speedMps * MPS_TO_KT).toFixed(0)} kt` : EM,
  },
  {
    id: 'vs', label: 'Vertical Speed', shortLabel: 'VS',
    description: 'GPS-derived rate of climb/descent, fpm. Two consecutive fixes required.',
    group: 'aircraft', available: true,
    compute: (c) => {
      const a = c.fix, b = c.prevFix;
      if (!a || !b || a.altMeters == null || b.altMeters == null) return EM;
      const dtSec = (a.timestamp - b.timestamp) / 1000;
      if (dtSec <= 0 || dtSec > 30) return EM; // stale / out-of-order
      const dAltFt = (a.altMeters - b.altMeters) * M_TO_FT;
      const fpm = (dAltFt / dtSec) * 60;
      const sign = fpm > 0 ? '+' : '';
      return `${sign}${fpm.toFixed(0)} fpm`;
    },
  },
  {
    id: 'turnRate', label: 'Rate of Turn', shortLabel: 'ROT',
    description: 'GPS-derived turn rate, deg/sec. Standard rate = 3.0\u00b0/sec.',
    group: 'aircraft', available: true,
    compute: (c) => {
      const a = c.fix, b = c.prevFix;
      if (!a || !b || a.headingDeg == null || b.headingDeg == null) return EM;
      const dtSec = (a.timestamp - b.timestamp) / 1000;
      if (dtSec <= 0 || dtSec > 10) return EM;
      let dTrk = a.headingDeg - b.headingDeg;
      // Normalize to (-180, 180] so a 359\u219201\u00b0 transition reads +2, not -358.
      if (dTrk > 180) dTrk -= 360;
      if (dTrk < -180) dTrk += 360;
      const rate = dTrk / dtSec;
      const sign = rate > 0 ? '+' : '';
      return `${sign}${rate.toFixed(1)}\u00b0/s`;
    },
  },
  {
    id: 'gMeter', label: 'G-Meter', shortLabel: 'G',
    description: 'Requires avionics integration (GDL90 AHRS) or iOS DeviceMotion permission.',
    group: 'aircraft', available: false,
    compute: () => EM,
  },

  // ---- Altitude ----
  {
    id: 'aglFt', label: 'Height AGL', shortLabel: 'AGL',
    description: 'GPS altitude minus terrain elevation at current position.',
    group: 'altitude', available: true,
    compute: (c) => {
      if (c.fix?.altMeters == null || c.terrainElevFt == null) return EM;
      const gpsFt = c.fix.altMeters * M_TO_FT;
      const agl = gpsFt - c.terrainElevFt;
      return `${agl.toFixed(0)} ft`;
    },
  },
  {
    id: 'pressureAlt', label: 'Pressure Altitude', shortLabel: 'PALT',
    description: 'Computed from GPS alt + nearest altimeter setting. Approximate.',
    group: 'altitude', available: true,
    compute: (c) => {
      if (c.fix?.altMeters == null || c.baroInHg == null) return EM;
      const gpsFt = c.fix.altMeters * M_TO_FT;
      // Pressure altitude = indicated alt at 29.92" Hg.
      // PA \u2248 GPS_alt + (29.92 - altimeter_setting) * 1000
      const pa = gpsFt + (29.92 - c.baroInHg) * 1000;
      return `${pa.toFixed(0)} ft (~)`;
    },
  },
  {
    id: 'baroAlt', label: 'Baro Altitude', shortLabel: 'BALT',
    description: 'Indicated altitude on a properly-set altimeter. Computed from GPS alt + nearest baro.',
    group: 'altitude', available: true,
    compute: (c) => {
      // For our purposes, baroAlt with the LOCAL altimeter setting is
      // approximately equal to GPS altitude (within \u00b150ft typically),
      // because GPS gives geometric altitude and baro alt with local
      // setting gives MSL altitude. We surface GPS alt with a (~) tag
      // so the user knows it's not a real static-port reading.
      if (c.fix?.altMeters == null) return EM;
      const gpsFt = c.fix.altMeters * M_TO_FT;
      return `${gpsFt.toFixed(0)} ft (~)`;
    },
  },
  {
    id: 'cabinPress', label: 'Cabin Pressure', shortLabel: 'CABIN',
    description: 'Requires panel avionics integration (Garmin Connext, etc.).',
    group: 'altitude', available: false,
    compute: () => EM,
  },
  {
    id: 'nearestBaro', label: 'Nearest Altimeter Setting', shortLabel: 'BARO',
    description: 'Altimeter setting from nearest reporting METAR station.',
    group: 'altitude', available: true,
    compute: (c) => c.baroInHg != null
      ? `${c.baroInHg.toFixed(2)}\u201d${c.baroStation ? ` ${c.baroStation}` : ''}`
      : EM,
  },
  {
    id: 'mefFt', label: 'Height MEF', shortLabel: 'MEF',
    description: 'Maximum Elevation Figure (sectional quadrangle). Coming with chart-data ingestion.',
    group: 'altitude', available: false,
    compute: () => EM,
  },
  {
    id: 'moraFt', label: 'Grid MORA', shortLabel: 'MORA',
    description: 'Maximum Off-Route Altitude. Coming with IFR enroute chart-data ingestion.',
    group: 'altitude', available: false,
    compute: () => EM,
  },

  // ---- Navigation: Next waypoint ----
  {
    id: 'distNext', label: 'Distance to Next', shortLabel: 'D-NXT',
    description: 'Great-circle distance from current position to the next plan waypoint, nm.',
    group: 'navigation', available: true,
    compute: (c) => {
      if (!c.fix || c.nextWpIdx < 0) return EM;
      const w = c.route[c.nextWpIdx];
      if (!w) return EM;
      const d = greatCircleNm(c.fix.lat, c.fix.lon, w.lat, w.lon);
      return `${d.toFixed(1)} nm`;
    },
  },
  {
    id: 'brgNext', label: 'Bearing to Next', shortLabel: 'B-NXT',
    description: 'Magnetic bearing from current position to the next waypoint.',
    group: 'navigation', available: true,
    compute: (c) => {
      if (!c.fix || c.nextWpIdx < 0) return EM;
      const w = c.route[c.nextWpIdx];
      if (!w) return EM;
      const tc = bearingDeg(c.fix.lat, c.fix.lon, w.lat, w.lon);
      const mh = trueToMagnetic(tc, c.magVarDeg);
      return `${pad3(mh)}\u00b0M`;
    },
  },
  {
    id: 'crsNext', label: 'Course to Next', shortLabel: 'C-NXT',
    description: 'Desired magnetic course on the active leg to the next waypoint.',
    group: 'navigation', available: true,
    compute: (c) => {
      if (c.nextWpIdx <= 0 || c.nextWpIdx >= c.route.length) return EM;
      const a = c.route[c.nextWpIdx - 1];
      const b = c.route[c.nextWpIdx];
      const tc = bearingDeg(a.lat, a.lon, b.lat, b.lon);
      const mh = trueToMagnetic(tc, c.magVarDeg);
      return `${pad3(mh)}\u00b0M`;
    },
  },
  {
    id: 'eteNext', label: 'ETE to Next', shortLabel: 'ETE-N',
    description: 'Time to next waypoint at current GPS groundspeed.',
    group: 'navigation', available: true,
    compute: (c) => {
      if (!c.fix || c.fix.speedMps == null || c.fix.speedMps <= 0) return EM;
      if (c.nextWpIdx < 0) return EM;
      const w = c.route[c.nextWpIdx];
      if (!w) return EM;
      const distNm = greatCircleNm(c.fix.lat, c.fix.lon, w.lat, w.lon);
      const gsKt = c.fix.speedMps * MPS_TO_KT;
      return formatHm((distNm / gsKt) * 60);
    },
  },
  {
    id: 'etaNext', label: 'ETA at Next', shortLabel: 'ETA-N',
    description: 'Estimated time of arrival at next waypoint, local time.',
    group: 'navigation', available: true,
    compute: (c) => {
      if (!c.fix || c.fix.speedMps == null || c.fix.speedMps <= 0) return EM;
      if (c.nextWpIdx < 0) return EM;
      const w = c.route[c.nextWpIdx];
      if (!w) return EM;
      const distNm = greatCircleNm(c.fix.lat, c.fix.lon, w.lat, w.lon);
      const gsKt = c.fix.speedMps * MPS_TO_KT;
      const eteMin = (distNm / gsKt) * 60;
      const eta = new Date(Date.now() + eteMin * 60_000);
      return formatLocalTime(eta);
    },
  },
  {
    id: 'cte', label: 'Cross-Track Error', shortLabel: 'XTE',
    description: 'Perpendicular distance from current position to the active leg, nm. Positive = right.',
    group: 'navigation', available: true,
    compute: (c) => {
      if (!c.fix || c.nextWpIdx <= 0 || c.nextWpIdx >= c.route.length) return EM;
      const a = c.route[c.nextWpIdx - 1];
      const b = c.route[c.nextWpIdx];
      const xte = crossTrackErrorNm(c.fix, a, b);
      const sign = xte > 0 ? 'R' : 'L';
      return `${sign} ${Math.abs(xte).toFixed(2)} nm`;
    },
  },

  // ---- Navigation: Destination ----
  {
    id: 'distDest', label: 'Distance to Dest', shortLabel: 'D-DST',
    description: 'Great-circle distance from current position to the final waypoint.',
    group: 'navigation', available: true,
    compute: (c) => {
      if (!c.fix || c.route.length === 0) return EM;
      const w = c.route[c.route.length - 1];
      const d = greatCircleNm(c.fix.lat, c.fix.lon, w.lat, w.lon);
      return `${d.toFixed(1)} nm`;
    },
  },
  {
    id: 'brgDest', label: 'Bearing to Dest', shortLabel: 'B-DST',
    description: 'Magnetic bearing from current position to the destination.',
    group: 'navigation', available: true,
    compute: (c) => {
      if (!c.fix || c.route.length === 0) return EM;
      const w = c.route[c.route.length - 1];
      const tc = bearingDeg(c.fix.lat, c.fix.lon, w.lat, w.lon);
      const mh = trueToMagnetic(tc, c.magVarDeg);
      return `${pad3(mh)}\u00b0M`;
    },
  },
  {
    id: 'eteDest', label: 'ETE to Dest', shortLabel: 'ETE-D',
    description: 'Time to destination at current GPS groundspeed (no winds, simple).',
    group: 'navigation', available: true,
    compute: (c) => {
      if (!c.fix || c.fix.speedMps == null || c.fix.speedMps <= 0) return EM;
      if (c.route.length === 0) return EM;
      const w = c.route[c.route.length - 1];
      const distNm = greatCircleNm(c.fix.lat, c.fix.lon, w.lat, w.lon);
      const gsKt = c.fix.speedMps * MPS_TO_KT;
      return formatHm((distNm / gsKt) * 60);
    },
  },
  {
    id: 'etaDest', label: 'ETA at Dest', shortLabel: 'ETA-D',
    description: 'Estimated arrival at destination, local time.',
    group: 'navigation', available: true,
    compute: (c) => {
      if (!c.fix || c.fix.speedMps == null || c.fix.speedMps <= 0) return EM;
      if (c.route.length === 0) return EM;
      const w = c.route[c.route.length - 1];
      const distNm = greatCircleNm(c.fix.lat, c.fix.lon, w.lat, w.lon);
      const gsKt = c.fix.speedMps * MPS_TO_KT;
      const eteMin = (distNm / gsKt) * 60;
      const eta = new Date(Date.now() + eteMin * 60_000);
      return formatLocalTime(eta);
    },
  },
  {
    id: 'descentDest', label: 'Descent to Dest', shortLabel: 'DESC',
    description: 'Required descent rate to arrive at destination elevation, fpm.',
    group: 'navigation', available: true,
    compute: (c) => {
      if (!c.fix || c.fix.altMeters == null || c.fix.speedMps == null || c.fix.speedMps <= 0) return EM;
      if (c.route.length === 0) return EM;
      const w = c.route[c.route.length - 1];
      // We use the destination's terrain elevation as a rough proxy
      // until we wire NASR airport-elevation lookup against the
      // destination ident. For en-route descents this is good enough
      // to give the pilot a meaningful number.
      const destElevFt = c.terrainElevFt; // fallback; better: lookup w.ident in NASR
      if (destElevFt == null) return EM;
      const distNm = greatCircleNm(c.fix.lat, c.fix.lon, w.lat, w.lon);
      const gsKt = c.fix.speedMps * MPS_TO_KT;
      const fpm = descentFpm(c.fix.altMeters * M_TO_FT, destElevFt, distNm, gsKt);
      if (fpm == null) return EM;
      return `${fpm > 0 ? '-' : '+'}${Math.abs(Math.round(fpm))} fpm`;
    },
  },
  {
    id: 'nearestApt', label: 'Nearest Airport', shortLabel: 'NRST APT',
    description: 'Closest airport from NASR + great-circle distance.',
    group: 'navigation', available: true,
    compute: (c) => c.nearestAirport
      ? `${c.nearestAirport.ident} ${c.nearestAirport.distNm.toFixed(1)}nm`
      : EM,
  },
  {
    id: 'nearestNav', label: 'Nearest Navaid', shortLabel: 'NRST NAV',
    description: 'Closest VOR/NDB/DME from NASR + distance.',
    group: 'navigation', available: true,
    compute: (c) => c.nearestNavaid
      ? `${c.nearestNavaid.ident} ${c.nearestNavaid.distNm.toFixed(1)}nm`
      : EM,
  },

  // ---- Time ----
  {
    id: 'flightTime', label: 'Flight Time', shortLabel: 'FT',
    description: 'Time since track recording started.',
    group: 'time', available: true,
    compute: (c) => {
      if (c.flightStartedAtMs == null) return EM;
      const sec = (Date.now() - c.flightStartedAtMs) / 1000;
      return formatHms(sec);
    },
  },

  // ---- Environment ----
  {
    id: 'horizonDist', label: 'Horizon Distance', shortLabel: 'HRZN',
    description: 'Visible-horizon distance ignoring terrain. 1.17 \u00d7 \u221a(altFt) nm (standard atmosphere).',
    group: 'environment', available: true,
    compute: (c) => {
      if (c.fix?.altMeters == null) return EM;
      const ftAGL = c.fix.altMeters * M_TO_FT;
      if (ftAGL <= 0) return '0 nm';
      const nm = 1.17 * Math.sqrt(ftAGL);
      return `${nm.toFixed(1)} nm`;
    },
  },
];

/** Keyed lookup. Cheaper than Array.find on every render. */
export const INSTRUMENT_BY_ID: Record<InstrumentId, InstrumentDef> = (() => {
  const m = {} as Record<InstrumentId, InstrumentDef>;
  for (const e of INSTRUMENT_CATALOG) m[e.id] = e;
  return m;
})();

/** Default selection for first-time users. Mirrors the original DataStrip. */
export const DEFAULT_INSTRUMENTS: InstrumentId[] = ['gs', 'track', 'gpsAlt', 'currCoords', 'flightTime'];

/** Hard cap matching the boss's spec. */
export const MAX_INSTRUMENTS = 5;

export function groupOrder(g: InstrumentGroup): number {
  return ['position', 'aircraft', 'altitude', 'navigation', 'time', 'environment'].indexOf(g);
}

export function groupLabel(g: InstrumentGroup): string {
  switch (g) {
    case 'position': return 'Position';
    case 'aircraft': return 'Aircraft';
    case 'altitude': return 'Altitude';
    case 'navigation': return 'Navigation';
    case 'time': return 'Time';
    case 'environment': return 'Environment';
  }
}
